import {XMarkIcon} from "@heroicons/react/20/solid";
import * as Headless from "@headlessui/react";
import {ReactNode} from "react";
import {OtsBrand, OtsColorSchemePreference, OtsThemeRoot} from "../theme/OtsTheme";
import {OtsIconActionButton} from "./OtsButton";

export type OtsDrawerProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** A line under the title, e.g. a count. */
  description?: ReactNode;
  /** Controls beside the close button, e.g. Refresh. */
  actions?: ReactNode;
  children: ReactNode;
  /** Pinned under the scrolling content. */
  footer?: ReactNode;
  side?: "right" | "left";
  size?: "md" | "lg";
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
};

/** A panel that slides in from the edge of the screen over the page, for something to glance at and dismiss. */
export const OtsDrawer = ({
  open,
  onClose,
  title,
  description,
  actions,
  children,
  footer,
  side = "right",
  size = "md",
  brand,
  colorScheme,
}: OtsDrawerProps) => (
  <Headless.Dialog open={open} onClose={onClose} className="relative z-[1250]">
    <Headless.DialogBackdrop transition className="fixed inset-0 bg-zinc-950/30 transition duration-150 data-closed:opacity-0"/>
    <div className={["fixed inset-y-0 flex max-w-full", side === "right" ? "right-0" : "left-0"].join(" ")}>
      <Headless.DialogPanel
        transition
        className={[
          "flex h-full w-screen flex-col shadow-2xl transition duration-200 ease-out",
          size === "lg" ? "max-w-xl" : "max-w-md",
          side === "right" ? "data-closed:translate-x-full" : "data-closed:-translate-x-full",
        ].join(" ")}>
        <OtsThemeRoot brand={brand} colorScheme={colorScheme} defaultColorScheme="system" className="flex h-full min-h-0 flex-col bg-[var(--ots-surface)]">
          <header className="!flex items-start gap-2 border-b border-[var(--ots-outline-variant)] px-4 py-3">
            <div className="min-w-0 flex-1">
              <Headless.DialogTitle className="truncate font-[family-name:var(--ots-font-display)] text-base font-semibold">{title}</Headless.DialogTitle>
              {description != null && <p className="truncate text-xs text-[var(--ots-text-muted)]">{description}</p>}
            </div>
            {actions}
            <OtsIconActionButton variant="text" size="compact" icon={<XMarkIcon/>} label="Close" onClick={onClose}/>
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>
          {footer != null && <footer className="border-t border-[var(--ots-outline-variant)] px-4 py-3 text-xs text-[var(--ots-text-muted)]">{footer}</footer>}
        </OtsThemeRoot>
      </Headless.DialogPanel>
    </div>
  </Headless.Dialog>
);
